import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DateSelector from "../components/DateSelector";
import DailyViewLayout from "../components/DailyViewLayout";
import DailyOverviewGrid from "../components/DailyOverviewGrid";
import DailyTurnoverChart from "../components/DailyTurnoverChart";
import DailyTurnoverTable from "../components/DailyTurnoverTable";
import { fetchDailyPlannings, fetchDailyHoursSummary } from "../api";
import { diffLocalDateTimeHours, formatLocalDate } from "../utils/localDateTime";

const DailyOverviewPage = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const selectedDate = date || formatLocalDate(new Date());

  const [plannings, setPlannings] = useState([]);
  const [hoursSummary, setHoursSummary] = useState([]);
  const [planningsLoading, setPlanningsLoading] = useState(true);
  const [hoursLoading, setHoursLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    if (!date || date !== selectedDate) {
      navigate(`/chart/daily/overview/${selectedDate}`, { replace: true });
    }
  }, [date, selectedDate, navigate]);

  // Reset loading states when date changes
  useEffect(() => {
    setChartLoading(true);
    setTableLoading(true);

    // Fetch plannings data
    setPlanningsLoading(true);
    fetchDailyPlannings(selectedDate)
      .then(setPlannings)
      .catch(() => setPlannings([]))
      .finally(() => setPlanningsLoading(false));

    // Fetch hours data
    setHoursLoading(true);
    fetchDailyHoursSummary(selectedDate)
      .then(setHoursSummary)
      .catch(() => setHoursSummary([]))
      .finally(() => setHoursLoading(false));
  }, [selectedDate]);

  const handleDateChange = useCallback((newDate) => {
    navigate(`/chart/daily/overview/${newDate}`);
  }, [navigate]);

  // Check if any data is still loading
  const isAnyLoading = planningsLoading || hoursLoading || chartLoading || tableLoading;

  const totalOps = plannings.filter(r => r.workcell_id !== "Total").reduce((sum, r) => sum + (r.total_completed || 0), 0);
  const totalTurnoverOps = plannings.filter(r => r.workcell_id !== "Total").reduce((sum, r) => sum + (r.internal_turnover || 0), 0);
  const totalHours = hoursSummary.reduce((sum, r) => sum + (r.active_hours || 0), 0);
  const totalPossibleHours = hoursSummary.reduce((sum, r) => sum + ((r.end_time && r.start_time) ? diffLocalDateTimeHours(r.start_time, r.end_time) : 0), 0);
  const totalTurnoverHours = hoursSummary.reduce((sum, r) => sum + (r.internal_turnover || 0), 0);
  const avgActivity = totalPossibleHours > 0 ? (totalHours / totalPossibleHours) * 100 : 0;

  return (
    <DailyViewLayout date={selectedDate}>
      <DateSelector date={selectedDate} setDate={handleDateChange} disabled={isAnyLoading} />
      <DailyOverviewGrid
        totalTurnoverOps={totalTurnoverOps}
        totalOps={totalOps}
        totalTurnoverHours={totalTurnoverHours}
        totalHours={totalHours}
        avgActivity={avgActivity}
        totalPossibleHours={totalPossibleHours}
      />
      <DailyTurnoverChart date={selectedDate} onLoadingChange={setChartLoading} />
      <DailyTurnoverTable date={selectedDate} onLoadingChange={setTableLoading} />
    </DailyViewLayout>
  );
};

export default DailyOverviewPage;
